import {
  AnchorDropdown,
  ApiErrorText,
  Button,
  CountrySelectInput,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
  Loader,
  SelectInput,
} from '@/shared/components';
import { getRequiredLabel, useToast } from '@/shared/lib';
import type {
  CurrencyOption,
  FormSteps,
  WithdrawBankTransferFormValues,
} from '../model';
import ContactBookIcon from '@/shared/assets/contact-book-icon.svg?react';
import { useForm } from 'react-hook-form';
import type { CommissionResponse, Country } from '@/shared/types';
import { cn } from '@/shared/utils';
import { useTranslation } from 'react-i18next';
import {
  formatValue,
  getMinAmount,
  getParamForTypeDetermination,
  isEditableParam,
  isParamInPrevStep,
  isParamSelect,
  isSelectAddress,
  isSelectBankCountry,
} from '../lib';
import {
  WithdrawAmountFormField,
  WithdrawIncludeFeeFormField,
} from '@/entities/operation';
import {
  BANK_ADDRESS_PARAM_IDS,
  type SavedBankAddress,
} from '@/entities/bank-address';
import EditIcon from '@/shared/assets/edit-icon.svg?react';
import type { ChangeEvent, Dispatch, SetStateAction } from 'react';

interface WithdrawBankTransferFormProps {
  form: ReturnType<typeof useForm<WithdrawBankTransferFormValues>>;
  assetOptions: CurrencyOption[];
  selectedCurrency?: CurrencyOption;
  hasWithdrawFee: boolean;
  isLoading: boolean;
  isError: boolean;
  error?: unknown | null;
  onMaxClick: () => void;
  commissionData?: CommissionResponse;
  formStepsData: FormSteps;
  currentStepIndex: number;
  savedBankAddresses: SavedBankAddress[];
  onEditSavedBankAddress: (savedBankAddress: SavedBankAddress) => void;
  countries?: Country[];
  setTemplateId: Dispatch<SetStateAction<string | undefined>>;
}

export function WithdrawBankTransferForm({
  form,
  isLoading,
  isError,
  error,
  formStepsData,
  currentStepIndex,
  onMaxClick,
  selectedCurrency,
  hasWithdrawFee,
  assetOptions,
  savedBankAddresses,
  onEditSavedBankAddress,
  countries,
  setTemplateId,
}: WithdrawBankTransferFormProps) {
  const currentStep =
    formStepsData[currentStepIndex] || formStepsData[formStepsData.length - 1];
  const formParams = currentStep?.params || [];
  const { toast, setPosition } = useToast();
  const { t } = useTranslation();

  if (isLoading) {
    return <Loader />;
  }

  if (isError) {
    return <ApiErrorText error={error} size="small" />;
  }

  const handleChange = (newValue: string) => {
    return {
      target: { value: newValue },
      currentTarget: { value: newValue },
    } as ChangeEvent<HTMLInputElement>;
  };

  return (
    <div className="flex flex-col gap-[1.6rem]">
      <Form {...form}>
        <form className="flex flex-col gap-[1.6rem]">
          {formParams.map(param => {
            if (!param.isVisible) {
              return null;
            }

            const paramForType = getParamForTypeDetermination(
              param,
              currentStepIndex,
              formStepsData
            );
            const isSelect = isParamSelect(paramForType);
            const isCountrySelect = isSelectBankCountry(paramForType);
            const isCurrencyCode = param.paramId === 'currencyCode';
            const isAddressSelect = isSelectAddress(param);
            const selectOptions = isCurrencyCode
              ? assetOptions
              : (param.valueList || []).map(val => ({ code: val }));
            const isEditable = isEditableParam(param);

            return (
              <FormField
                key={param.paramId}
                control={form.control}
                name={param.paramId}
                render={({ field, fieldState }) => {
                  const isPrevStepParam = isParamInPrevStep(
                    param,
                    currentStepIndex,
                    formStepsData
                  );
                  const isDisabled = !isEditable || isPrevStepParam;
                  const disabledParamClass = cn(
                    isPrevStepParam && 'pointer-events-none'
                  );

                  const placeholder = getRequiredLabel(
                    param.paramName,
                    !!param.isRequired
                  );

                  const handleEditToast = (
                    e: React.MouseEvent<HTMLDivElement>
                  ) => {
                    if (isPrevStepParam) {
                      e.preventDefault();
                      e.stopPropagation();
                      const message = t(
                        'withdrawMethods.bankTransfer.editToastMessage'
                      );
                      setPosition('top-center');
                      toast.dismiss();
                      toast.default(message);
                    }
                  };

                  const fieldValue = (field.value as string) || '';

                  return (
                    <FormItem
                      className={cn(
                        param.isVisible || param.isvivsible ? '' : 'hidden'
                      )}>
                      <FormControl>
                        <div onClick={handleEditToast}>
                          {isCountrySelect ? (
                            <CountrySelectInput
                              {...field}
                              onValueChange={value => {
                                field.onChange(value);
                              }}
                              placeholder={placeholder}
                              countries={countries ?? []}
                              disabled={isDisabled}
                              value={fieldValue}
                              error={fieldState.error}
                            />
                          ) : isSelect ? (
                            <SelectInput
                              {...field}
                              onValueChange={value => {
                                field.onChange(value);
                                if (isCurrencyCode) {
                                  setTemplateId('');
                                }
                              }}
                              value={fieldValue}
                              triggerClassName={disabledParamClass}
                              onClickCapture={handleEditToast}
                              floatingLabel={placeholder}
                              options={selectOptions}
                              disabled={isDisabled}
                              valueKey="code"
                              selectedKey="code"
                              error={fieldState.error}
                            />
                          ) : isAddressSelect ? (
                            <AnchorDropdown<SavedBankAddress>
                              triggerIcon={
                                <ContactBookIcon className="size-[2.4rem]" />
                              }
                              disabledTrigger={isDisabled}
                              options={savedBankAddresses ?? []}
                              getOptionKey={option => option.templateId}
                              contentClassName="max-h-[28.8rem]"
                              renderOption={option => (
                                <div className="py-[1.4rem] px-[2.4rem] flex items-center justify-between min-h-[7.2rem] rounded-[1.6rem] hover:bg-secondary-bg">
                                  <div className="flex flex-col">
                                    <span className="headline4 text-primary">
                                      {option.templateName}
                                    </span>
                                    <span className="p5 text-secondary">
                                      {option.params.find(
                                        p =>
                                          p.paramId ===
                                          BANK_ADDRESS_PARAM_IDS.bankName
                                      )?.paramValue || ''}
                                    </span>
                                  </div>

                                  <Button
                                    variant="icon"
                                    size="icon-button"
                                    onClick={() =>
                                      onEditSavedBankAddress(option)
                                    }
                                    className="w-[4.4rem] h-[4.4rem] hover:bg-secondary-bg text-tertiary hover:text-primary">
                                    <EditIcon className="size-[2.4rem]" />
                                  </Button>
                                </div>
                              )}
                              onSelect={option => {
                                form.setValue(
                                  BANK_ADDRESS_PARAM_IDS.currencyCode,
                                  option.params.find(
                                    p =>
                                      p.paramId ===
                                      BANK_ADDRESS_PARAM_IDS.currencyCode
                                  )?.paramValue || ''
                                );
                                form.setValue(
                                  field.name,
                                  option.params.find(
                                    p => p.paramId === field.name
                                  )?.paramValue || ''
                                );
                                setTemplateId(option.templateId);
                              }}>
                              {trigger => (
                                <Input
                                  {...field}
                                  onChange={e => {
                                    field.onChange(
                                      handleChange(
                                        formatValue(
                                          e.target.value,
                                          param?.inputFormat
                                        )
                                      )
                                    );
                                    setTemplateId('');
                                  }}
                                  className={disabledParamClass}
                                  disabled={isDisabled}
                                  label={placeholder}
                                  value={fieldValue}
                                  aria-invalid={!!fieldState.error}
                                  rightContent={trigger}
                                />
                              )}
                            </AnchorDropdown>
                          ) : (
                            <Input
                              {...field}
                              onChange={e => {
                                field.onChange(
                                  handleChange(
                                    formatValue(
                                      e.target.value,
                                      param?.inputFormat
                                    )
                                  )
                                );
                              }}
                              aria-invalid={!!fieldState.error}
                              className={disabledParamClass}
                              disabled={isDisabled}
                              label={placeholder}
                              value={fieldValue}
                            />
                          )}
                        </div>
                      </FormControl>
                      <FormMessage className="px-[1.6rem]" />
                    </FormItem>
                  );
                }}
              />
            );
          })}

          {currentStep?.isDisplayAmount && (
            <>
              <WithdrawAmountFormField
                control={form.control}
                name="amount"
                selectedAsset={
                  selectedCurrency
                    ? {
                        code:
                          currentStep?.amountCurrency || selectedCurrency.code,
                        available: selectedCurrency.available,
                        min: getMinAmount(currentStep, selectedCurrency),
                      }
                    : undefined
                }
                onMaxClick={onMaxClick}
                availableBalanceTranslationKey="withdrawMethods.card.availableBalance"
              />

              <WithdrawIncludeFeeFormField
                control={form.control}
                name="includeFee"
                visible={currentStep?.isDisplayAmount && hasWithdrawFee}
              />
            </>
          )}
        </form>
      </Form>
    </div>
  );
}
